import { ChangeDetectorRef, Injectable } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { DialogConfirmComponent } from '../components/dialogs/dialog-confirm/dialog-confirm.component';
import { DialogInfoComponent } from '../components/dialogs/dialog-info/dialog-info.component';
import { ConfirmOptions, DialogOptions, PromptOptions } from '../models/dialog.model';
import { DialogPromptComponent } from '../components/dialogs/dialog-prompt/dialog-prompt.component';
import { AppInsightsService } from './app-insights.service';
import { FindAndReplaceDialogComponent } from '../components/dialogs/find-and-replace-dialog/find-and-replace-dialog.component';

@Injectable({ providedIn: 'root' })
export class DialogService {
  _defaultModalOptions: NgbModalOptions = {
    centered: false,
    scrollable: true,
  };

  public fileMenuRef: NgbOffcanvasRef | undefined;

  private openedModalRefs: NamedDialog[] = [];

  private readonly DialogNameConfirm = 'internal_confirm';
  private readonly DialogNameInfo = 'internal_InfoDialog';
  private readonly DialogNamePrompt = 'internal_promptDialogName';

  constructor(private modalService: NgbModal, private logger: AppInsightsService) {}

  public openCustomDialog(
    name: string,
    component: any,
    options?: NgbModalOptions,
    closeCallback?: Function
  ): NgbModalRef {
    if (this.openedModalRefs.some((x) => x.name === name)) {
      return this.openedModalRefs.find((x) => x.name === name)!.modal;
    }
    if (!options) {
      options = this._defaultModalOptions;
    }
    const newOptions = { ...this._defaultModalOptions, ...options };
    const modalRef = this.modalService.open(component, newOptions);
    modalRef.result.finally(() => {
      closeCallback && closeCallback();
      // remove the modal from the opened modals list
      this.openedModalRefs = this.openedModalRefs.filter((x) => x.name !== name);
      this.eventCloseDialog();
    });
    this.openedModalRefs.push({ name, modal: modalRef });
    return modalRef;
  }

  public confirm(options: ConfirmOptions, customName = this.DialogNameConfirm): Promise<any> {
    const confirmModalRef = this.openCustomDialog(customName, DialogConfirmComponent, {
      ...this._defaultModalOptions,
      size: options.size || 'sm',
      backdrop: 'static',
    });
    const modalInstance = confirmModalRef.componentInstance;
    modalInstance.title = options.title;
    modalInstance.message = options.message;
    modalInstance.confirmButtonText = options.confirmButtonText || `Yes`;
    modalInstance.rejectButtonText = options.rejectButtonText || `No`;
    modalInstance.showHeaderCloseButton = options.showHeaderCloseButton;

    confirmModalRef.result
      .then(
        () => {
          this.logger.logTrace('ConfirmDialogUserAction', {
            customName: this.DialogNameConfirm,
            modalMessage: options?.message,
            confirmGiven: options?.title,
            userAction: 'User confirmed',
          });
          options.confirmCallback();
        },
        () => {
          this.logger.logTrace('ConfirmDialogUserAction', {
            customName: this.DialogNameConfirm,
            modalMessage: options?.message,
            confirmGiven: options?.title,
            userAction: 'User rejected',
          });
          options.rejectCallback('');
        }
      )
      .finally(() => {
        this.eventCloseDialog();
      });
    return confirmModalRef.result;
  }
  public eventCloseDialog() {
    // this is to force TXText to gain focus and resync the keyboard state
    // Insert key is chosen due to it not toggling the overwrite mode and not effecting the cursor state.
    let targetElement = document.querySelector('div#txHiddenTextField_textView.txHiddenTextField');
    if (targetElement != null) {
      // Create a new KeyboardEvent for the 'Insert' key
      let insertEvent = new KeyboardEvent('keyup', {
        key: 'Insert',
        code: 'Insert',
        keyCode: 45, // The key code for 'Insert' key
        which: 45,
        bubbles: true, // Ensure the event bubbles up through the DOM
        cancelable: true,
      });
      targetElement.dispatchEvent(insertEvent);
    }
  }
  public info(options: DialogOptions): Promise<void> {
    const infoModalRef = this.openCustomDialog(this.DialogNameInfo, DialogInfoComponent, {
      ...this._defaultModalOptions,
      size: options.size || 'md',
    });
    const modalInstance = infoModalRef.componentInstance;
    modalInstance.title = options.title;
    modalInstance.message = options.message;

    return infoModalRef.result.finally(() => {
      this.eventCloseDialog();
    });
  }

  public prompt(options: PromptOptions): Promise<string> {
    const promptModalRef = this.openCustomDialog(this.DialogNamePrompt, DialogPromptComponent, {
      ...this._defaultModalOptions,
      size: options.size || 'md',
    });
    const modalInstance = promptModalRef.componentInstance;
    modalInstance.title = options.title;
    modalInstance.message = options.message;
    modalInstance.confirmButtonText = options.confirmButtonText || `Yes`;
    modalInstance.rejectButtonText = options.rejectButtonText || `No`;
    modalInstance.showHeaderCloseButton = options.showHeaderCloseButton;
    return new Promise<string>((resolve, reject) => {
      promptModalRef.result
        .then(
          (result) => {
            this.logger.logTrace('PromptDialogUserAction', {
              modalMessage: options?.message,
              promptGiven: options?.title,
              userAction: 'User confirmed',
            });
            options.confirmCallback && options.confirmCallback(modalInstance.returnValue);
            resolve(result as string);
          },
          (reason) => {
            this.logger.logTrace('PromptDialogUserAction', {
              modalMessage: options?.message,
              promptGiven: options?.title,
              userAction: 'User rejected',
            });
            options.rejectCallback && options.rejectCallback();
            reject(reason);
          }
        )
        .finally(() => {
          this.eventCloseDialog();
        });
    });
  }

  public closeDialogs(reason = '') {
    if (this.fileMenuRef) {
      this.fileMenuRef.dismiss();
    }
    this.modalService.dismissAll(reason);
  }

  get isConfirmOpen() {
    return this.isDialogOpen(this.DialogNameConfirm);
  }

  get isInfoOpen() {
    return this.isDialogOpen(this.DialogNameInfo);
  }

  get isPromptOpen() {
    return this.isDialogOpen(this.DialogNamePrompt);
  }

  public isAnyDialogOpen(): boolean {
    return this.openedModalRefs.length > 0;
  }

  public openFindReplaceDialog(mode: 'find' | 'replace', closeCallback?: Function) {
    const modalRef = this.openCustomDialog(
      'replace',
      FindAndReplaceDialogComponent,
      {
        modalDialogClass: 'find-and-replace-modal-dialog',
        size: 'lg',
        backdrop: false,
        scrollable: false,
      },
      () => {
        closeCallback && closeCallback();
      }
    );
    const modalInstance: FindAndReplaceDialogComponent = modalRef.componentInstance;
    modalInstance.activeTab = mode;
  }

  public isDialogOpen(name: string): boolean {
    return this.openedModalRefs.some((x) => x.name === name);
  }
}

interface NamedDialog {
  name: string;
  modal: NgbModalRef;
}
